import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { usePDPData } from '~/features/product-details/context';
import { getSalesforceProductImages } from '~/lib/salesforce/utilities/product-images';
import { CompareToggle } from '~/shared/components/NewProductCard/components';
import { ComparisonItem } from '~/shared/productComparison';
import { usePage } from '~/templates/pages';

type Props = Omit<React.ComponentProps<typeof CompareToggle>, 'item'>;

/**
 * The comparison toggle for the product being viewed, so a customer can start a comparison
 * from the product page as well as from a list.
 *
 * Reads the product from context rather than taking it as a prop, for the same reason
 * `ProductWishlistButton` does: it keeps the store subscription - and the re-render on every
 * toggle - inside this button instead of the whole overview.
 */
export const ProductCompareButton: React.FC<Props> = (props) => {
    const { product } = usePDPData();
    // `product.url` isn't populated on the PDP's own product data - we're already on the
    // product's page, so its current path is the URL to link back to from the tray.
    const { asPath } = useRouter();
    const { productCategoryLinks } = usePage();

    const item = useMemo<ComparisonItem>(() => {
        const [image] = getSalesforceProductImages(product, { limit: 1 }) ?? [];

        // Same fallback order `DynamicPage`'s dead-product redirect uses: the most specific
        // category available, not the brand - a pot from Eva Trio should send the customer
        // back to "Grydesæt", not to the brand's own page.
        const { subCategory, series, category, brand } = productCategoryLinks ?? {};
        const categoryLink = subCategory || series || category || brand;

        return {
            id: product.id,
            name: product.name ?? product.id,
            url: product.url ?? asPath,
            brand: product.brand,
            image: image?.link
                ? { src: image.link, alt: image.alt ?? image.title ?? '' }
                : undefined,
            categoryUrl: categoryLink?.url,
        };
    }, [product, asPath, productCategoryLinks]);

    // The PDP always shows the icon + "Sammenlign" look, on every viewport - unlike the grid,
    // there's no card layout here that needs the more compact mobile checkbox treatment.
    return <CompareToggle {...props} item={item} variant="ghost" />;
};
